import { useState } from 'react';
import type { ModChange, ModEntry, ModResolveResult } from '@mcpanel/core';
import { AddonSearch, AddonIcon, SourceTag } from '../components/AddonSearch';
import { Dialog } from '../components/ui';
import { Spinner } from '../components/feedback';
import { useEditDirty } from '../components/EditBoundary';
import { velocityApi } from '../api/velocity';
import { errorMessage, useApp } from '../app-context';

export function VelocityPlugins({ entries, reload }: { entries: ModEntry[]; reload: () => void }) {
  const { canOperate, notify } = useApp();
  const [add, setAdd] = useState<Array<{ project: string; title: string }>>([]);
  const [remove, setRemove] = useState<string[]>([]);
  const [update, setUpdate] = useState<string[]>([]);
  const [preview, setPreview] = useState<Omit<ModResolveResult, 'installed'> & { previewHash: string }>();
  const [busy, setBusy] = useState(false);
  useEditDirty(Boolean(add.length || remove.length || update.length));
  const change = (): ModChange => ({
    kind: 'plugin',
    add: add.map((m) => ({ project: m.project, source: 'modrinth' as const })),
    remove,
    update,
  });
  async function resolve() {
    setBusy(true);
    try {
      setPreview(await velocityApi.preview(change()));
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  }
  async function apply() {
    setBusy(true);
    try {
      await velocityApi.plugins(change(), preview!.previewHash);
      setPreview(undefined);
      setAdd([]);
      setRemove([]);
      setUpdate([]);
      reload();
      notify('Velocity のプラグインを反映しました');
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="stack">
      <section className="card">
        <div className="card-header">
          <h2>導入済みのプラグイン（{entries.length}）</h2>
        </div>
        <div className="table-wrap">
          <table>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.source + entry.projectId}>
                  <td>
                    <div className="row">
                      <AddonIcon url={entry.iconUrl} title={entry.title} />
                      <span>
                        {entry.title} <SourceTag source={entry.source} />
                        {entry.dependency && '（依存）'}
                        <br />
                        {entry.versionNumber}
                      </span>
                    </div>
                  </td>
                  <td>
                    {canOperate && !entry.dependency && (
                      <div className="row">
                        <button
                          className="btn small"
                          onClick={() =>
                            setUpdate(
                              update.includes(entry.projectId)
                                ? update.filter((id) => id !== entry.projectId)
                                : [...update, entry.projectId],
                            )
                          }
                        >
                          {update.includes(entry.projectId) ? '更新を取り消す' : '更新'}
                        </button>
                        <button
                          className="btn small danger"
                          onClick={() =>
                            setRemove(
                              remove.includes(entry.projectId)
                                ? remove.filter((id) => id !== entry.projectId)
                                : [...remove, entry.projectId],
                            )
                          }
                        >
                          {remove.includes(entry.projectId) ? '削除を取り消す' : '削除'}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="card-body small muted">Panel が配置した jar だけを管理します。設定フォルダは保持します。</p>
      </section>
      {add.map((m) => (
        <div className="row" key={m.project}>
          {m.title}（追加予定）
          <button className="btn small" onClick={() => setAdd(add.filter((item) => item !== m))}>
            取り消す
          </button>
        </div>
      ))}
      {canOperate && (
        <AddonSearch
          family="velocity"
          kind="plugin"
          mcVersion="velocity"
          sources={['modrinth']}
          stateOf={(hit) =>
            entries.some((m) => m.projectId === hit.projectId)
              ? 'installed'
              : add.some((m) => m.project === hit.projectId)
                ? 'staged'
                : undefined
          }
          onAdd={(hit) => setAdd([...add, { project: hit.projectId, title: hit.title }])}
        />
      )}
      {canOperate && Boolean(add.length || remove.length || update.length) && (
        <button className="btn primary" disabled={busy} onClick={() => void resolve()}>
          {busy && <Spinner />}依存関係を確認
        </button>
      )}
      {preview && (
        <Dialog
          title="プラグインの変更内容"
          onClose={() => setPreview(undefined)}
          actions={
            <>
              <button className="btn" onClick={() => setPreview(undefined)}>
                戻る
              </button>
              <button className="btn primary" disabled={busy || preview.errors.length > 0} onClick={() => void apply()}>
                {busy && <Spinner />}反映して再起動
              </button>
            </>
          }
        >
          {preview.errors.map((e) => (
            <p className="alert danger" key={e.project}>
              {e.project}: {e.message}
            </p>
          ))}
          <p>接続中の全プレイヤーが切断されます。</p>
          <h3>追加</h3>
          {preview.added.map((m) => (
            <p key={m.projectId}>
              {m.title} {m.versionNumber}
              {m.dependency && '（依存で自動追加）'}
            </p>
          ))}
          <h3>削除</h3>
          {preview.removed.map((m) => (
            <p key={m.projectId}>{m.title}</p>
          ))}
          <h3>更新</h3>
          {preview.updated.map((m) => (
            <p key={m.after.projectId}>
              {m.after.title}: {m.before.versionNumber} → {m.after.versionNumber}
            </p>
          ))}
          <p>変更後: {preview.entries.length} 件</p>
        </Dialog>
      )}
    </div>
  );
}
